import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { clearUserDataCache } from './utils';
import { sessionChecker } from './utils/sessionChecker';
import { usePermissionsStore } from './stores/permissionsStore';
import { useCalendarStore } from './stores/calendarStore';
import { clearTwoFactorSession, set2faPendingFlag, get2faPendingFlag, get2faPendingFlagRaw, clear2faPendingFlag } from './hooks/useTwoFactor';
import { rememberOAuthReturnPath } from './utils/oauthReturnPath';
import { useActiveRoleStore } from './stores/activeRoleStore';

// Define types for our contexts
interface UserDataContextType {
  organizationId: string | null;
  userProfile: any | null;
  userId: string | null;
  isSuperAdmin: boolean;
  isPartnerAdmin: boolean;
  partnerAdminId: string | null;
  /**
   * true finché l'auth non ha risolto sessione+profilo. I route guard che
   * decidono sull'organizationId (BetaVoiceAgentsRoute, BetaAgentsRoute,
   * EnrichmentRoute) DEVONO aspettarlo: prima di questo campo destrutturavano
   * `loading` inesistente (sempre undefined) → su hard-load/refresh il gate
   * decideva con organizationId ancora null → redirect spurio a "/" (bug
   * "apro l'agente e mi torna in dashboard", 17 lug 2026).
   */
  loading: boolean;
}

interface TwoFactorState {
  required: boolean;
  configured: boolean;
  setupCompleted: boolean;
  method: 'totp' | 'email' | 'both' | null;
  availableMethods: ('totp' | 'email')[];
}

interface SignInResult {
  error?: any;
  requiresTwoFactorSetup?: boolean;
  requiresTwoFactorVerification?: boolean;
  twoFactorState?: TwoFactorState;
  email?: string;
}

type OAuthProvider = 'google' | 'apple' | 'azure';

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  isSuperAdmin: boolean;
  isPartnerAdmin: boolean;
  partnerAdminId: string | null;
  userProfile: any;
  twoFactorPending: { email: string; state: TwoFactorState } | null;
  // Valori reali dal DB — per route guard e hasDualRole
  rawIsSuperAdmin: boolean;
  rawIsPartnerAdmin: boolean;
  rawPartnerAdminId: string | null;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** returnTo: pagina richiesta prima del login, ripresa da /auth/callback (vedi utils/oauthReturnPath.ts) */
  signInWithOAuth: (provider: OAuthProvider, options?: { loginHint?: string; returnTo?: string | null }) => Promise<{ error?: any }>;
  signUp: (email: string, password: string, userData?: any) => Promise<{data?: any, error?: any}>;
  signOut: () => Promise<void>;
  updateProfile: (data: any) => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearTwoFactorPending: () => void;
  completeTwoFactorVerification: () => void;
}

// Create a context to store user organization and profile data
export const UserContext = createContext<UserDataContextType | undefined>(undefined);

export function useUserData() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within the UserContext.Provider');
  }
  return context;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isPartnerAdmin, setIsPartnerAdmin] = useState(false);
  const [partnerAdminId, setPartnerAdminId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [twoFactorPending, setTwoFactorPending] = useState<{ email: string; state: TwoFactorState } | null>(null);

  // Track last processed user ID to prevent duplicate fetches on tab switch
  const lastProcessedUserId = useRef<string | null>(null);

  useEffect(() => {
    let isUnmounted = false;
    let isFetching = false; // Flag per prevenire fetch multipli

    // Salva il timestamp di inizio sessione per la gestione ottimizzata dei permessi
    if (typeof window !== 'undefined' && !window.sessionStorage.getItem('session_start_time')) {
      window.sessionStorage.setItem('session_start_time', Date.now().toString());
    }

    const fetchUserData = async (userId) => {
      // Previeni fetch multiple simultanee
      if (isFetching) {
        return;
      }

      if (!userId) {
        if (!isUnmounted) {
          setLoading(false);
        }
        return;
      }

      isFetching = true; // Imposta flag
      try {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, organization_id, is_super_admin, is_partner_admin, seller_partner_id, active, full_name, email, avatar_url, profile_image_url, onboarding_state, onboarding_checklist, section_hints_state')
          .eq('id', userId)
          .single();

        if (profileError) {
          console.error('[Auth] Error fetching user profile:', profileError);
          if (!isUnmounted) {
            setIsSuperAdmin(false);
            setIsPartnerAdmin(false);
            setPartnerAdminId(null);
            setOrganizationId(null);
            setUserProfile(null);
            setLoading(false);
          }
          return;
        }

        if (!profile) {
          console.error('[Auth] No profile found for user:', userId);
          if (!isUnmounted) {
            setIsSuperAdmin(false);
            setIsPartnerAdmin(false);
            setPartnerAdminId(null);
            setOrganizationId(null);
            setUserProfile(null);
            setLoading(false);
          }
          return;
        }

        if (!isUnmounted) {
          // Check if user is active before setting profile data
          if (profile.active === false) {
            console.warn('[Auth] User account is deactivated, signing out');
            await supabase.auth.signOut({ scope: 'local' });
            setUser(null);
            setSession(null);
            setIsSuperAdmin(false);
            setIsPartnerAdmin(false);
            setPartnerAdminId(null);
            setOrganizationId(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }

          setIsSuperAdmin(profile.is_super_admin || false);
          setIsPartnerAdmin(profile.is_partner_admin || false);
          setPartnerAdminId(profile.seller_partner_id || null);
          setOrganizationId(profile.organization_id);
          setUserProfile(profile);

          // Fetch permissions after setting profile data
          // Per dual-role: usa effective values basati su activeRole dallo store (tab-aware:
          // sessionStorage in modalità impersonate, localStorage altrimenti). NON leggere
          // direttamente da localStorage perché la tab impersonate erediterebbe il valore
          // dalla tab admin causando UI da superadmin per l'utente target.
          const currentActiveRole = useActiveRoleStore.getState().activeRole;
          const profileIsDualRole = !!profile.organization_id && (profile.is_super_admin || profile.is_partner_admin);
          // null = default CRM mode per sicurezza
          const effectiveSuperAdminForPerms = profileIsDualRole && currentActiveRole !== 'admin' ? false : (profile.is_super_admin || false);
          const effectiveOrgIdForPerms = profileIsDualRole && currentActiveRole === 'admin' ? null : profile.organization_id;

          const permissionsStore = usePermissionsStore.getState();
          await permissionsStore.fetchPermissions(userId, effectiveSuperAdminForPerms, effectiveOrgIdForPerms);

          setLoading(false);
        }
      } catch (error) {
        console.error('[Auth] Error fetching user data:', error);
        if (!isUnmounted) {
          setIsSuperAdmin(false);
          setIsPartnerAdmin(false);
          setPartnerAdminId(null);
          setOrganizationId(null);
          setUserProfile(null);
          setLoading(false);
        }
      } finally {
        isFetching = false; // Reset flag
      }
    };

    const fetchSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isUnmounted) {
        // Check if 2FA verification was pending (user reloaded/closed browser mid-2FA)
        // Nota: il flag pending viene settato dal signIn con password E dal login SSO
        // (OAuthCallback, dal 16 lug 2026 il 2FA è uno step successivo anche per
        // Google/Microsoft). In entrambi i casi viene pulito a verifica riuscita.
        // Ripristino soft (31 lug 2026): flag valido → la sessione resta "sospesa"
        // (twoFactorPending settato subito, ProtectedRoute blocca tutto tranne
        // /login e /2fa-setup) e la Login riapre lo step OTP. Flag SCADUTO →
        // signOut fail-closed: la verifica non è mai stata completata, la
        // finestra è chiusa e la sessione non deve sopravvivere.
        if (session?.user) {
          const pendingFlag = get2faPendingFlagRaw();
          if (pendingFlag && pendingFlag.userId === session.user.id) {
            if (pendingFlag.expired) {
              console.warn('[Auth] 2FA pending flag expired on session restore. Signing out.');
              clear2faPendingFlag();
              await supabase.auth.signOut({ scope: 'local' });
              setSession(null);
              setUser(null);
              setLoading(false);
              return;
            }
            console.warn('[Auth] 2FA verification pending on session restore. Keeping session suspended.');
            setTwoFactorPending(prev => prev ?? {
              email: session.user.email ?? '',
              state: {
                required: true,
                configured: true,
                setupCompleted: true,
                method: null,
                availableMethods: ['totp', 'email']
              }
            });
          }
          // Clean up orphaned flag for different user
          if (pendingFlag && pendingFlag.userId !== session.user.id) {
            clear2faPendingFlag();
          }
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          fetchUserData(session.user.id);
        } else {
          setIsSuperAdmin(false);
          setOrganizationId(null);
          setUserProfile(null);
          setLoading(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isUnmounted) {
        const permissionsStore = usePermissionsStore.getState();

        // Handle auth events with exclusive branching to prevent unnecessary permission fetches
        if (event === 'SIGNED_OUT') {
          // On logout: clear permissions and reset all state
          permissionsStore.clearPermissions();
          lastProcessedUserId.current = null; // Reset tracking
          console.log('[Auth] Permissions cleared on SIGNED_OUT');

          setSession(null);
          setUser(null);
          setIsSuperAdmin(false);
          setOrganizationId(null);
          setUserProfile(null);
          setTwoFactorPending(null);
          setLoading(false);

        } else if (event === 'TOKEN_REFRESHED') {
          // Token refresh: ONLY update session/user, DON'T fetch permissions
          // Permissions haven't changed, this is just a JWT renewal
          console.log('[Auth] Token refreshed - session updated, permissions retained');

          setSession(session as any);
          setUser((session?.user ?? null) as any);
          // Do NOT call fetchUserData - permissions are already loaded

        } else if (event === 'USER_UPDATED') {
          // User update: only refresh permissions if organization changed
          const currentOrgId = organizationId;
          const newOrgId = session?.user?.user_metadata?.organization_id;

          setSession(session as any);
          setUser((session?.user ?? null) as any);

          if (currentOrgId && newOrgId && currentOrgId !== newOrgId) {
            console.log('[Auth] Organization changed, refreshing user data and permissions');
            if (session?.user?.id) {
              fetchUserData(session.user.id);
            }
          } else {
            console.log('[Auth] User updated but organization unchanged - permissions retained');
          }

        } else {
          // Other events (SIGNED_IN, INITIAL_SESSION, etc)
          const newUserId = session?.user?.id ?? null;

          // Fail-closed sincrono: se INITIAL_SESSION arriva prima che fetchSession
          // abbia marcato il 2FA pendente, ProtectedRoute vedrebbe un utente
          // "pulito" e renderizzerebbe contenuto protetto per qualche istante.
          // Raw getter: il flag scaduto NON va auto-pulito qui (lo gestisce
          // fetchSession con signOut); nel dubbio blocchiamo comunque.
          if (session?.user) {
            const pendingFlag = get2faPendingFlagRaw();
            if (pendingFlag && pendingFlag.userId === session.user.id) {
              setTwoFactorPending(prev => prev ?? {
                email: session.user.email ?? '',
                state: {
                  required: true,
                  configured: true,
                  setupCompleted: true,
                  method: null,
                  availableMethods: ['totp', 'email']
                }
              });
            }
          }

          setSession(session as any);
          setUser((session?.user ?? null) as any);

          if (session?.user) {
            // Only fetch if it's a new user or first load
            if (lastProcessedUserId.current !== newUserId) {
              console.log('[Auth] Auth event:', event, '- new user, fetching data and permissions');
              lastProcessedUserId.current = newUserId;
              fetchUserData(newUserId);
            } else {
              console.log('[Auth] Auth event:', event, '- same user, skipping fetch (session retained)');
              // Session already exists, no need to re-fetch
            }
          } else {
            lastProcessedUserId.current = null;
            setIsSuperAdmin(false);
            setOrganizationId(null);
            setUserProfile(null);
            setLoading(false);
          }
        }
      }
    });

    fetchSession();

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error };

      // Skip 2FA in development mode
      if (import.meta.env.DEV) {
        return { user: data.user, session: data.session };
      }

      // Check 2FA status
      try {
        const { data: twoFaStatus, error: twoFaError } = await supabase.functions.invoke('twofa-get-status', {
          headers: {
            Authorization: `Bearer ${data.session?.access_token}`
          }
        });

        if (twoFaError) {
          console.error('[Auth] Error fetching 2FA status:', twoFaError);
          // In produzione, blocca il login se non possiamo verificare lo stato 2FA
          return { error: new Error('Impossibile verificare lo stato 2FA. Riprova.') };
        }

        // 2FA is required for everyone
        if (twoFaStatus?.required) {
          const state: TwoFactorState = {
            required: twoFaStatus.required,
            configured: twoFaStatus.configured,
            setupCompleted: twoFaStatus.setupCompleted,
            method: twoFaStatus.method,
            // Use enabledMethods for verification (methods actually configured)
            // Fall back to availableMethods for setup flow
            availableMethods: twoFaStatus.setupCompleted
              ? (twoFaStatus.enabledMethods || twoFaStatus.availableMethods || ['totp', 'email'])
              : (twoFaStatus.availableMethods || ['totp', 'email'])
          };

          if (!twoFaStatus.setupCompleted) {
            // 2FA required but not configured -> needs setup
            setTwoFactorPending({ email, state });
            set2faPendingFlag(data.user!.id);
            return {
              error: null,
              requiresTwoFactorSetup: true,
              twoFactorState: state,
              email
            };
          } else {
            // 2FA configured -> needs verification
            setTwoFactorPending({ email, state });
            set2faPendingFlag(data.user!.id);
            return {
              error: null,
              requiresTwoFactorVerification: true,
              twoFactorState: state,
              email
            };
          }
        }
      } catch (twoFaCheckError) {
        console.error('[Auth] 2FA check failed:', twoFaCheckError);
        // In produzione, blocca il login se la verifica 2FA fallisce
        return { error: new Error('Errore verifica 2FA. Riprova.') };
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  }, []);

  const signInWithOAuth = useCallback(async (provider: OAuthProvider, options?: { loginHint?: string; returnTo?: string | null }): Promise<{ error?: any }> => {
    try {
      // Il giro dal provider perde lo stato del router: la pagina richiesta resta in sessionStorage
      // (solo percorsi interni). Senza returnTo toglie un valore rimasto da un tentativo precedente.
      rememberOAuthReturnPath(options?.returnTo ?? null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'email profile',
          ...(options?.loginHint ? { queryParams: { login_hint: options.loginHint } } : {}),
        },
      });
      if (error) return { error };
      return {};
    } catch (error) {
      return { error };
    }
  }, []);

  const signUp = useCallback(async (email, password, userData) => {
    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: userData?.full_name || '' } },
      });
      return { data: authData, error };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signOut = useCallback(async () => {
    // Ripristina lo sfondo predefinito
    if (typeof document !== 'undefined') {
      document.body.style.background = `
        hsla(0,100%,50%,1);
        background-image:
          radial-gradient(at 40% 20%, hsla(28,100%,74%,1) 0px, transparent 50%),
          radial-gradient(at 80% 0%, hsla(189,100%,56%,1) 0px, transparent 50%),
          radial-gradient(at 0% 50%, hsla(355,100%,93%,1) 0px, transparent 50%),
          radial-gradient(at 80% 50%, hsla(340,100%,76%,1) 0px, transparent 50%),
          radial-gradient(at 0% 100%, hsla(22,100%,77%,1) 0px, transparent 50%),
          radial-gradient(at 80% 100%, hsla(242,100%,70%,1) 0px, transparent 50%),
          radial-gradient(at 0% 0%, hsla(343,100%,76%,1) 0px, transparent 50%);
      `;
    }

    // Cleanup session checker
    sessionChecker.cleanup();

    // Clear 2FA session
    clearTwoFactorSession();
    clear2faPendingFlag();
    setTwoFactorPending(null);

    // Esegui il logout effettivo (scope: 'local' per non invalidare sessioni mobile)
    await supabase.auth.signOut({ scope: 'local' });
    setUser(null);
    setSession(null);
    setIsSuperAdmin(false);
    setOrganizationId(null);
    setUserProfile(null);

    // Clear localStorage items that could contain organization-specific data
    localStorage.removeItem('settings-storage');
    localStorage.removeItem('whatsapp_initial_sync_done');
    // Pulisci il ruolo attivo (localStorage + Zustand store)
    useActiveRoleStore.getState().clearActiveRole();

    // Note: Permissions are cleared automatically by onAuthStateChange listener on SIGNED_OUT event

    // Svuota cache notifiche WhatsApp
    if (window.clearWhatsAppNotificationCache) {
      window.clearWhatsAppNotificationCache();
    }

    // Clear all cached user data from sessionStorage, including WhatsApp
    clearUserDataCache();

    // Reset session start time
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('session_start_time');

      // Per sicurezza, pulisci tutta la sessionStorage
      sessionStorage.clear();

      // Pulisci anche tutti gli item di localStorage che potrebbero contenere dati dell'utente
      Object.keys(localStorage).forEach(key => {
        if (key.includes('whatsapp') || key.includes('chat') ||
            key.includes('message') || key.includes('background') ||
            key.includes('settings') || key.includes('user') ||
            key.includes('calendar')) {
          localStorage.removeItem(key);
        }
      });
    }

    // Reset calendar store (clears cache and state)
    useCalendarStore.getState().resetStore();
  }, []);

  const updateProfile = useCallback(async (data) => {
    try {
      await supabase.auth.updateUser({ data });
      await supabase
        .from('user_profiles')
        .update(data)
        .eq('id', user?.id);

      // Update local state with new profile data
      setUserProfile(prev => ({...prev, ...data}));
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  }, [user?.id]);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('id, organization_id, is_super_admin, is_partner_admin, seller_partner_id, active, full_name, email, two_factor_enabled, two_factor_method, onboarding_state, onboarding_checklist, section_hints_state')
        .eq('id', user.id)
        .single();

      if (profile) {
        setUserProfile(profile);
        setIsSuperAdmin(profile.is_super_admin || false);
        setIsPartnerAdmin(profile.is_partner_admin || false);
        setPartnerAdminId(profile.seller_partner_id || null);
        setOrganizationId(profile.organization_id);
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  }, [user?.id]);

  const clearTwoFactorPending = useCallback(() => {
    setTwoFactorPending(null);
    clear2faPendingFlag();
  }, []);

  const completeTwoFactorVerification = useCallback(() => {
    setTwoFactorPending(null);
    clear2faPendingFlag();
    // Refresh profile to get updated 2FA status
    refreshProfile();
  }, [refreshProfile]);

  // activeRole dallo Zustand store (reattivo)
  const activeRole = useActiveRoleStore(state => state.activeRole);

  // authValue con effective values calcolati dentro il useMemo per corretta memoizzazione
  const authValue: AuthContextType = useMemo(() => {
    const isDualRole = !!organizationId && (isSuperAdmin || isPartnerAdmin);

    let effSA = isSuperAdmin;
    let effPA = isPartnerAdmin;
    let effPAId = partnerAdminId;

    if (isDualRole && (activeRole === 'crm' || activeRole === null)) {
      // CRM mode o pre-scelta: nascondi flag admin (default CRM per sicurezza)
      effSA = false;
      effPA = false;
      effPAId = null;
    }

    return {
      user,
      session,
      loading,
      isSuperAdmin: effSA,
      isPartnerAdmin: effPA,
      partnerAdminId: effPAId,
      userProfile,
      twoFactorPending,
      rawIsSuperAdmin: isSuperAdmin,
      rawIsPartnerAdmin: isPartnerAdmin,
      rawPartnerAdminId: partnerAdminId,
      signIn,
      signInWithOAuth,
      signUp,
      signOut,
      updateProfile,
      refreshProfile,
      clearTwoFactorPending,
      completeTwoFactorVerification,
    };
  }, [user, session, loading, isSuperAdmin, isPartnerAdmin, partnerAdminId, organizationId, activeRole, userProfile, twoFactorPending, signIn, signInWithOAuth, signUp, signOut, updateProfile, refreshProfile, clearTwoFactorPending, completeTwoFactorVerification]);

  const userDataValue: UserDataContextType = useMemo(() => {
    const isDualRole = !!organizationId && (isSuperAdmin || isPartnerAdmin);

    let effOrgId = organizationId;
    let effSA = isSuperAdmin;
    let effPA = isPartnerAdmin;
    let effPAId = partnerAdminId;

    if (isDualRole && activeRole === 'admin') {
      // Admin mode: nascondi org
      effOrgId = null;
    }
    if (isDualRole && (activeRole === 'crm' || activeRole === null)) {
      // CRM mode o pre-scelta: nascondi flag admin
      effSA = false;
      effPA = false;
      effPAId = null;
    }

    return {
      organizationId: effOrgId,
      userProfile,
      userId: user?.id,
      isSuperAdmin: effSA,
      isPartnerAdmin: effPA,
      partnerAdminId: effPAId,
      loading,
    };
  }, [organizationId, userProfile, user?.id, isSuperAdmin, isPartnerAdmin, partnerAdminId, activeRole, loading]);

  return React.createElement(
    AuthContext.Provider, 
    { value: authValue },
    React.createElement(
      UserContext.Provider,
      { value: userDataValue },
      children
    )
  );
}